import {
  createPayrollEntry,
  deletePayrollEntry,
  getPayrollTimesheetPreview,
  getPayrollTimesheetState,
  getTimesheetChangeLog,
  getTimesheetPunchHistory,
  getTimesheetRevisionByID,
  getTimesheetRevisions,
  submitTimesheetForEmployee,
  updatePayrollEntry,
} from '../api/timesheetEdit';
import {
  PayrollEditEntryKind,
  PayrollEntryBody,
  PayrollEntryResult,
  PayrollSubmitTimesheetRequest,
  PayrollSubmitTimesheetResponse,
  PayrollTimesheetState,
  PunchHistoryResponse,
  TimesheetChangeLogResponse,
  TimesheetPreview,
  TimesheetRevisionDetailResponse,
  TimesheetRevisionListResponse,
} from '../interfaces/TimesheetEditInterface';

/**
 * Payroll Timesheet Modifications — data layer (Milestone 1, M1-B2).
 * House pattern: log, rethrow the original error (status codes stay readable
 * for the 409/400/403 designed states).
 */

const fetchState = async (
  employeeID: number,
  payPeriodID: number
): Promise<PayrollTimesheetState> => {
  try {
    const apiResults = await getPayrollTimesheetState(employeeID, payPeriodID);

    if (!apiResults) {
      throw new Error('Load Payroll Timesheet State Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in getPayrollTimesheetState', err);
    throw err;
  }
};

const submitForEmployee = async (
  request: PayrollSubmitTimesheetRequest
): Promise<PayrollSubmitTimesheetResponse> => {
  try {
    const apiResults = await submitTimesheetForEmployee(request);

    if (!apiResults || !apiResults.success) {
      throw new Error('Submit Timesheet For Employee Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in submitTimesheetForEmployee', err);
    throw err;
  }
};

const fetchPreview = async (
  employeeID: number,
  payPeriodID: number
): Promise<TimesheetPreview> => {
  try {
    const apiResults = await getPayrollTimesheetPreview(employeeID, payPeriodID);

    if (!apiResults) {
      throw new Error('Load Payroll Timesheet Preview Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in getPayrollTimesheetPreview', err);
    throw err;
  }
};

const fetchPunchHistory = async (
  employeeID: number,
  payPeriodID: number
): Promise<PunchHistoryResponse> => {
  try {
    const apiResults = await getTimesheetPunchHistory(employeeID, payPeriodID);

    if (!apiResults) {
      throw new Error('Load Punch History Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in getTimesheetPunchHistory', err);
    throw err;
  }
};

const fetchChangeLog = async (
  employeeID: number,
  payPeriodID: number
): Promise<TimesheetChangeLogResponse> => {
  try {
    const apiResults = await getTimesheetChangeLog(employeeID, payPeriodID);

    if (!apiResults) {
      throw new Error('Load Timesheet Change Log Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in getTimesheetChangeLog', err);
    throw err;
  }
};

const fetchRevisions = async (
  employeeID: number,
  payPeriodID: number
): Promise<TimesheetRevisionListResponse> => {
  try {
    const apiResults = await getTimesheetRevisions(employeeID, payPeriodID);

    if (!apiResults) {
      throw new Error('Load Timesheet Revisions Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in getTimesheetRevisions', err);
    throw err;
  }
};

const fetchRevisionByID = async (
  timesheetRevisionSnapshotID: number
): Promise<TimesheetRevisionDetailResponse> => {
  try {
    const apiResults = await getTimesheetRevisionByID(timesheetRevisionSnapshotID);

    if (!apiResults) {
      throw new Error('Load Timesheet Revision Failed.');
    }

    return apiResults;
  } catch (err) {
    console.error('Error in getTimesheetRevisionByID', err);
    throw err;
  }
};

// ── Milestone 2 — entry CRUD (§8). Same house pattern: log, rethrow the
// ORIGINAL error so the designed 409/400 states keep their status + message.

const createEntry = async (
  kind: PayrollEditEntryKind,
  body: PayrollEntryBody
): Promise<PayrollEntryResult> => {
  try {
    const apiResults = await createPayrollEntry(kind, body);

    if (!apiResults || !apiResults.success) {
      throw new Error(`Create Payroll ${kind} Entry Failed.`);
    }

    return apiResults;
  } catch (err) {
    console.error('Error in createPayrollEntry', err);
    throw err;
  }
};

const updateEntry = async (
  kind: PayrollEditEntryKind,
  entityID: number,
  body: PayrollEntryBody
): Promise<PayrollEntryResult> => {
  try {
    const apiResults = await updatePayrollEntry(kind, entityID, body);

    if (!apiResults || !apiResults.success) {
      throw new Error(`Update Payroll ${kind} Entry Failed.`);
    }

    return apiResults;
  } catch (err) {
    console.error('Error in updatePayrollEntry', err);
    throw err;
  }
};

const deleteEntry = async (
  kind: PayrollEditEntryKind,
  entityID: number,
  editSessionID: string
): Promise<PayrollEntryResult> => {
  try {
    const apiResults = await deletePayrollEntry(kind, entityID, editSessionID);

    if (!apiResults || !apiResults.success) {
      throw new Error(`Delete Payroll ${kind} Entry Failed.`);
    }

    return apiResults;
  } catch (err) {
    console.error('Error in deletePayrollEntry', err);
    throw err;
  }
};

export {
  fetchState, fetchPreview, submitForEmployee, fetchChangeLog, fetchPunchHistory, fetchRevisions,
  fetchRevisionByID, createEntry, updateEntry, deleteEntry,
};
